import type {
  ConversationMessage,
  PublicWidgetConfig,
  SendMessageResponse,
} from '@cbm/types';
import {
  initChatResponseSchema,
  publicWidgetConfigSchema,
  sendMessageResponseSchema,
} from '@cbm/types';

export type PollConversationUpdatesResponse = {
  state: 'ai_active' | 'waiting_for_human' | 'human_active' | 'closed';
  messages: ConversationMessage[];
};

type JsonRequestInit = Omit<RequestInit, 'body'> & {
  json?: Record<string, unknown>;
};

async function postJson<T>(
  apiBaseUrl: string,
  path: string,
  schema: { parse: (input: unknown) => T } | undefined,
  init: JsonRequestInit,
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}/${path}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    ...init,
    body: init.json ? JSON.stringify(init.json) : undefined,
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Widget request failed: ${response.status}`);
  }

  const json = (await response.json()) as unknown;
  return schema ? schema.parse(json) : (json as T);
}

export async function fetchPublicConfig(
  apiBaseUrl: string,
  widgetKey: string,
): Promise<PublicWidgetConfig> {
  return postJson(apiBaseUrl, 'fetchPublicConfig', publicWidgetConfigSchema, {
    json: {
      widgetKey,
      origin: window.location.origin,
    },
  });
}

export async function initChat(
  apiBaseUrl: string,
  payload: {
    widgetKey: string;
    sessionId?: string;
    visitorId?: string;
  },
) {
  return postJson(apiBaseUrl, 'initChat', initChatResponseSchema, {
    json: {
      ...payload,
      origin: window.location.origin,
      currentUrl: window.location.href,
    },
  });
}

export async function sendMessage(
  apiBaseUrl: string,
  payload: {
    widgetKey: string;
    projectId: string;
    conversationId: string;
    conversationToken: string;
    content: string;
  },
): Promise<SendMessageResponse> {
  return postJson(apiBaseUrl, 'sendMessage', sendMessageResponseSchema, {
    json: {
      ...payload,
      origin: window.location.origin,
      currentUrl: window.location.href,
    },
  });
}

export async function submitLead(
  apiBaseUrl: string,
  payload: {
    widgetKey: string;
    projectId: string;
    conversationId: string;
    conversationToken: string;
    name: string;
    email: string;
    phone?: string;
    company?: string;
    note?: string;
  },
): Promise<{ ok: true }> {
  return postJson(apiBaseUrl, 'submitLead', undefined, {
    json: payload,
  });
}

export async function pollConversationUpdates(
  apiBaseUrl: string,
  payload: {
    widgetKey: string;
    projectId: string;
    conversationId: string;
    conversationToken: string;
    after?: string;
  },
): Promise<PollConversationUpdatesResponse> {
  return postJson(apiBaseUrl, 'pollConversationUpdates', undefined, {
    json: payload,
  });
}
